import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import LivePreview from "@/components/LivePreview";

const projects = [
  {
    title: "Luxury Auto Shine",
    label: "Demo Website",
    desc: "A premium luxury auto detailing website created as a portfolio demonstration.",
    link: "https://luxury-auto-shine-4.preview.emergentagent.com/?utm_source=share",
    image:
      "https://images.unsplash.com/photo-1741356474349-3ca53f0601ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBhYnN0cmFjdCUyMGJsYWNrJTIwZ29sZCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg0ODg4NzQ5fDA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-7",
  },
  {
    title: "Elite Barber",
    label: "Demo Website",
    desc: "A modern barber shop landing page created to showcase premium web design.",
    link: "https://premium-cuts-co-2.preview.emergentagent.com/?utm_source=share",
    image:
      "https://images.unsplash.com/photo-1761437856376-2ce1c483343b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBhYnN0cmFjdCUyMGJsYWNrJTIwZ29sZCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg0ODg4NzQ5fDA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-5",
  },
];

function ProjectCard({ p, index }) {
  return (
    <motion.a
      href={p.link}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`portfolio-card-${index}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className={`group relative block ${p.span}`}
    >
      <div className="relative overflow-hidden rounded-3xl border border-white/10 glass-card">
        <div className="relative h-[360px] md:h-[520px] overflow-hidden">
          <LivePreview url={p.link} poster={p.image} title={p.title} />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none" />

          <span
            data-testid={`portfolio-label-${index}`}
            className="absolute top-6 left-6 px-4 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-[#d4af37]/40 text-xs uppercase tracking-[0.2em] gold-text pointer-events-none"
          >
            {p.label}
          </span>
          <span className="absolute top-6 right-6 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15 text-[10px] uppercase tracking-[0.2em] text-white/70 pointer-events-none">
            Live
          </span>

          <div className="absolute inset-x-0 bottom-0 p-7 md:p-10 pointer-events-none">
            <h3 className="font-serif-display text-3xl md:text-5xl mb-3 tracking-tight">
              {p.title}
            </h3>
            <p className="text-white/60 max-w-md mb-7 leading-relaxed">{p.desc}</p>
            <span
              data-testid={`portfolio-demo-btn-${index}`}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#d4af37] text-black font-medium tracking-wide group-hover:bg-[#f3e5ab] transition-colors duration-300"
            >
              Live Demo
              <ArrowUpRight
                size={18}
                className="group-hover:rotate-45 transition-transform duration-300"
              />
            </span>
          </div>
        </div>
      </div>
    </motion.a>
  );
}

export default function Portfolio() {
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-[#d4af37]" />
              <span className="text-xs uppercase tracking-[0.3em] gold-text">Selected Work</span>
            </div>
            <h2 className="font-serif-display font-light text-4xl md:text-6xl tracking-tight">
              The <span className="italic gold-text">Portfolio</span>
            </h2>
          </div>
          <p className="max-w-sm text-white/50 leading-relaxed">
            A curated selection of demo projects, each crafted to demonstrate premium,
            conversion-focused design.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {projects.map((p, i) => (
            <ProjectCard key={p.title} p={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
